import {Box, CardContent, TextField, Typography} from "@mui/material";
import {EditForm} from "@variocube/app-ui";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useAsync} from "react-async-hook";
import {GeoLocation, useLocationApi} from "../../api";
import {useLocalization} from "@smalox/shared";
import {useNumericParam} from "../../utils/useParam";

// Fix Leaflet default marker icon paths (known Vite bundler issue)
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: markerIcon2x,
	iconUrl: markerIcon,
	shadowUrl: markerShadow,
});

const DEFAULT_CENTER: [number, number] = [48.2082, 16.3738]; // Vienna
const DEFAULT_ZOOM = 13;

export function LocationGeoLocation() {
	const {t} = useLocalization();
	const locationId = useNumericParam("locationId");
	const {getLocationGeoLocation, updateLocationGeoLocation} = useLocationApi();

	const {loading, result: geoLocation} = useAsync(getLocationGeoLocation, [locationId]);

	const [latitude, setLatitude] = useState<string>("");
	const [longitude, setLongitude] = useState<string>("");
	const [altitude, setAltitude] = useState<string>("");

	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<L.Map | null>(null);
	const markerRef = useRef<L.Marker | null>(null);

	// Sync state from loaded geoLocation
	useEffect(() => {
		if (geoLocation) {
			setLatitude(geoLocation.latitude != null ? String(geoLocation.latitude) : "");
			setLongitude(geoLocation.longitude != null ? String(geoLocation.longitude) : "");
			setAltitude(geoLocation.altitude != null ? String(geoLocation.altitude) : "");
		}
	}, [geoLocation]);

	const updateMarker = useCallback((lat: number, lng: number) => {
		if (markerRef.current && mapRef.current) {
			markerRef.current.setLatLng([lat, lng]);
		}
	}, []);

	// Initialize map
	useEffect(() => {
		if (!mapContainerRef.current || mapRef.current) return;

		const center: [number, number] =
			geoLocation?.latitude != null && geoLocation?.longitude != null
				? [geoLocation.latitude, geoLocation.longitude]
				: DEFAULT_CENTER;

		const map = L.map(mapContainerRef.current).setView(center, DEFAULT_ZOOM);
		mapRef.current = map;

		L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
			attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
			maxZoom: 19,
		}).addTo(map);

		const marker = L.marker(center, {draggable: true}).addTo(map);
		markerRef.current = marker;

		marker.on("dragend", () => {
			const pos = marker.getLatLng();
			setLatitude(String(pos.lat));
			setLongitude(String(pos.lng));
		});

		map.on("click", (e: L.LeafletMouseEvent) => {
			const {lat, lng} = e.latlng;
			marker.setLatLng([lat, lng]);
			setLatitude(String(lat));
			setLongitude(String(lng));
		});

		return () => {
			map.remove();
			mapRef.current = null;
			markerRef.current = null;
		};
	}, [geoLocation]);

	// Sync marker position when fields change
	useEffect(() => {
		const lat = parseFloat(latitude);
		const lng = parseFloat(longitude);
		if (!isNaN(lat) && !isNaN(lng)) {
			updateMarker(lat, lng);
		}
	}, [latitude, longitude, updateMarker]);

	async function handleSave() {
		const geo: GeoLocation = {
			latitude: latitude ? parseFloat(latitude) : undefined,
			longitude: longitude ? parseFloat(longitude) : undefined,
			altitude: altitude ? parseFloat(altitude) : undefined,
		};
		await updateLocationGeoLocation(locationId, geo);
	}

	return (
		<EditForm loading={loading} labels={t} onSave={handleSave}>
			<CardContent>
				<Typography variant="body2" color="textSecondary" gutterBottom>
					{t("geoLocation.dragHint")}
				</Typography>
				<Box
					ref={mapContainerRef}
					sx={{
						height: 400,
						width: "100%",
						mb: 2,
						borderRadius: 1,
						overflow: "hidden",
					}}
				/>
			</CardContent>
			<CardContent>
				<TextField
					label={t("geoLocation.latitude")}
					value={latitude}
					onChange={e => setLatitude(e.target.value)}
					type="number"
					fullWidth
				/>
			</CardContent>
			<CardContent>
				<TextField
					label={t("geoLocation.longitude")}
					value={longitude}
					onChange={e => setLongitude(e.target.value)}
					type="number"
					fullWidth
				/>
			</CardContent>
			<CardContent>
				<TextField
					label={t("geoLocation.altitude")}
					value={altitude}
					onChange={e => setAltitude(e.target.value)}
					type="number"
					fullWidth
				/>
			</CardContent>
		</EditForm>
	);
}
